import React from 'react';
import { Link } from 'react-router-dom';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MAChristianCounseling = () => {
  const coreCourses = [
    { code: 'MIN 503', name: 'Advanced Theology of Ministry', credits: 3 },
    { code: 'MIN 505', name: 'Advanced Contemporary Preaching and Teaching', credits: 3 },
    { code: 'MIN 507', name: 'Leadership in the Church', credits: 3 },
    { code: 'MIN 509', name: '21st Century Evangelism, Discipleship, and Church Growth', credits: 3 },
    { code: 'MIN 602', name: 'Church Administration', credits: 3 },
    { code: 'MIN 606', name: 'Recruiting and Training Volunteers', credits: 3 },
    { code: 'MIN 615', name: 'Use of Small Groups in Church Building', credits: 3 },
  ];

  const electiveAreas = [
    {
      area: 'Christian Counseling/Psychology',
      courses: [{ code: 'MIN 521', name: 'Advanced Christian Counseling', credits: 3 }]
    },
    {
      area: 'Christian Education',
      courses: [{ code: 'MIN 533', name: 'Developing a Total Church Education Program', credits: 3 }]
    },
    {
      area: 'Christian Missions',
      courses: [{ code: 'MIN 541', name: 'Strategies for Global Missions', credits: 3 }]
    }
  ];

  const thesisCourses = [
    { code: 'MIN 591', name: 'Thesis, Practicum, or Project I', credits: 3 },
    { code: 'MIN 592', name: 'Thesis, Practicum, or Project II', credits: 3 }
  ];

  const resources = [
    {
      title: "Course Descriptions",
      description: "Detailed information about each course in the program",
      path: "/academics/ma_christian_counseling/courses"
    },
    {
      title: "Program Handbook",
      description: "Complete guide to program requirements and policies",
      path: "/academics/ma_christian_counseling/handbook"
    },
    {
      title: "Clinical Training",
      description: "Information about practicum and internship opportunities",
      path: "/academics/ma_christian_counseling/clinical"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Christian Counseling
            </h1>
            <p className="text-xl opacity-90">
              Advanced study combining theological foundations with counseling psychology
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                Learn more about our{' '}
                <InlineLink to="/academics/ma_christian_counseling/curriculum">curriculum structure</InlineLink> and{' '}
                <InlineLink to="/academics/ma_christian_counseling/outcomes">learning outcomes</InlineLink>.
              </p>
            </div>
          </section>

          {/* Curriculum Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Curriculum</h2>

            {/* Core Courses */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">Core Courses (21 credits)</h3>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course Name</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {coreCourses.map((course, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{course.code}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{course.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{course.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Elective Areas */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">Minor Elective Areas (9 credits)</h3>
              <div className="space-y-6">
                {electiveAreas.map((area, index) => (
                  <div key={index}>
                    <h4 className="text-lg font-medium mb-3 text-primary-600">{area.area}</h4>
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <tbody className="bg-white">
                          {area.courses.map((course, courseIndex) => (
                            <tr key={courseIndex}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{course.code}</td>
                              <td className="px-6 py-4 text-sm text-gray-900">{course.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{course.credits}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Thesis Section */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">Thesis Requirement (6 credits)</h3>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white">
                    {thesisCourses.map((course, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{course.code}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{course.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{course.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Total Credits */}
            <div className="bg-primary-600 text-white rounded-xl p-6 flex justify-between items-center">
              <span className="text-lg font-semibold">Total Credits Required for Degree</span>
              <span className="text-2xl font-bold">36</span>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Take the first step towards your Master's degree in Christian Counseling.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MAChristianCounseling; 